import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
};

const heightBySize: Record<Size, number> = { sm: 28, md: 32 };
const paddingBySize: Record<Size, string> = { sm: '0 10px', md: '0 12px' };
const fontBySize: Record<Size, number> = { sm: 12, md: 13 };

const variantStyles = (variant: Variant, disabled: boolean): React.CSSProperties => {
  const base: React.CSSProperties = {
    border: '1px solid transparent',
    transition: 'background 120ms ease-out, border-color 120ms ease-out, color 120ms ease-out',
  };
  if (disabled) {
    return { ...base, background: 'var(--shell-bg-subtle)', color: 'var(--shell-text-tertiary)' };
  }
  switch (variant) {
    case 'primary':
      return {
        ...base,
        background: 'var(--shell-accent-primary)',
        color: 'var(--shell-accent-on-accent)',
      };
    case 'secondary':
      return {
        ...base,
        background: 'var(--shell-bg-surface)',
        color: 'var(--shell-text-primary)',
        borderColor: 'var(--shell-border-default)',
      };
    case 'ghost':
      return {
        ...base,
        background: 'transparent',
        color: 'var(--shell-text-secondary)',
      };
    case 'danger':
      return {
        ...base,
        background: 'var(--shell-bg-surface)',
        color: 'var(--shell-semantic-error)',
        borderColor: 'var(--shell-border-default)',
      };
  }
};

const Button = ({
  variant = 'secondary',
  size = 'md',
  leftIcon,
  rightIcon,
  fullWidth,
  disabled,
  children,
  style,
  ...rest
}: ButtonProps) => {
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: heightBySize[size],
        padding: paddingBySize[size],
        borderRadius: 'var(--shell-radius-md)',
        fontSize: fontBySize[size],
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: fullWidth ? '100%' : 'auto',
        whiteSpace: 'nowrap',
        ...variantStyles(variant, !!disabled),
        ...style,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        const t = e.currentTarget;
        if (variant === 'primary') t.style.background = 'var(--shell-accent-hover)';
        else if (variant === 'secondary') t.style.background = 'var(--shell-bg-hover)';
        else if (variant === 'ghost') {
          t.style.background = 'var(--shell-bg-hover)';
          t.style.color = 'var(--shell-text-primary)';
        } else if (variant === 'danger') {
          t.style.background = 'var(--shell-semantic-error)';
          t.style.color = '#fff';
          t.style.borderColor = 'var(--shell-semantic-error)';
        }
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        const t = e.currentTarget;
        const s = variantStyles(variant, false);
        t.style.background = (s.background as string) ?? '';
        t.style.color = (s.color as string) ?? '';
        t.style.borderColor = (s.borderColor as string) ?? 'transparent';
      }}
      {...rest}
    >
      {leftIcon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{leftIcon}</span>}
      {children}
      {rightIcon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{rightIcon}</span>}
    </button>
  );
};

export default Button;
